import React, { useState, useEffect } from 'react'
import { FileData } from '@/types'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError } from '@/utils/toast'
import { CertificateListDisplay } from './CertificateDisplay'
import { DuplicateCertificateModal } from './DuplicateCertificateModal'

interface RootCertificatesSectionProps {
  initialCertificates?: FileData[]
  onSave: (certificates: FileData[]) => Promise<void>
  readOnly?: boolean
}

/**
 * Generates a unique filename by appending a number if the filename already exists
 */
const generateUniqueFilename = (filename: string, existingFilenames: string[]): string => {
  const lowerExisting = existingFilenames.map(f => f.toLowerCase())
  if (!lowerExisting.includes(filename.toLowerCase())) {
    return filename
  }

  const extension = filename.substring(filename.lastIndexOf('.'))
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'))
  let counter = 1
  let newFilename = `${nameWithoutExt}_${counter}${extension}`

  while (lowerExisting.includes(newFilename.toLowerCase())) {
    counter++
    newFilename = `${nameWithoutExt}_${counter}${extension}`
  }

  return newFilename
}

export const RootCertificatesSection: React.FC<RootCertificatesSectionProps> = ({
  initialCertificates,
  onSave,
  readOnly = false,
}) => {
  const [isSaving, setIsSaving] = useState(false)

  /** Editable working state */
  const [rootCertificates, setRootCertificates] = useState<FileData[]>(initialCertificates || [])

  /** Last successfully submitted certificates */
  const [submittedCertificates, setSubmittedCertificates] = useState<FileData[]>(
    initialCertificates || []
  )

  /** Files selected in FileInput */
  const [rootCertificateFiles, setRootCertificateFiles] = useState<File[]>([])

  /** Dirty flag */
  const [isDirty, setIsDirty] = useState(false)

  const [duplicateModal, setDuplicateModal] = useState<{
    filename: string
    newCertificate: FileData
    existingIndex: number
    fileIndex: number
  } | null>(null)

  const [pendingCertificates, setPendingCertificates] = useState<FileData[]>([])
  const [pendingFileIndices, setPendingFileIndices] = useState<number[]>([])
  const [removedIndices, setRemovedIndices] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (initialCertificates) {
      setRootCertificates(initialCertificates)
      setSubmittedCertificates(initialCertificates)
      setRemovedIndices(new Set())
      setIsDirty(false)
    }
  }, [initialCertificates])

  const markDirty = () => setIsDirty(true)

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const startingFileIndex = rootCertificateFiles.length
    setRootCertificateFiles(prev => [...prev, ...fileArray])

    const filePromises = fileArray.map(
      (file): Promise<FileData> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1]
            resolve({ filename: file.name, data: base64 })
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
    )

    try {
      const newCertificates = await Promise.all(filePromises)
      const allCertificates = [...rootCertificates]
      const existingFilenames = allCertificates.map(cert => cert.filename || '')

      for (let i = 0; i < newCertificates.length; i++) {
        const newCert = newCertificates[i]
        const existingIndex = existingFilenames.findIndex(
          name => name.toLowerCase() === (newCert.filename || '').toLowerCase()
        )

        if (existingIndex !== -1) {
          setDuplicateModal({
            filename: newCert.filename || '',
            newCertificate: newCert,
            existingIndex,
            fileIndex: startingFileIndex + i,
          })

          setPendingCertificates(newCertificates.slice(i + 1))
          setPendingFileIndices(
            newCertificates.slice(i + 1).map((_, j) => startingFileIndex + i + 1 + j)
          )
          return
        }

        allCertificates.push(newCert)
        existingFilenames.push(newCert.filename || '')
      }

      setRootCertificates(allCertificates)
      markDirty()
    } catch (error) {
      console.error(error)
      toastError('Erreur lors de la lecture des fichiers de certificats')
    }
  }

  const processPendingCertificates = (
    currentCertificates: FileData[],
    pending: FileData[],
    fileIndices: number[]
  ) => {
    const allCertificates = [...currentCertificates]
    const existingFilenames = allCertificates.map(cert => cert.filename || '')
    const updatedFiles = [...rootCertificateFiles]

    for (let i = 0; i < pending.length; i++) {
      const cert = pending[i]
      const fileIndex = fileIndices[i]

      const duplicate = existingFilenames.find(
        name => name.toLowerCase() === (cert.filename || '').toLowerCase()
      )

      if (!duplicate) {
        allCertificates.push(cert)
        existingFilenames.push(cert.filename || '')
      } else {
        const uniqueFilename = generateUniqueFilename(cert.filename || '', existingFilenames)
        allCertificates.push({ ...cert, filename: uniqueFilename })
        existingFilenames.push(uniqueFilename)

        if (fileIndex >= 0 && fileIndex < updatedFiles.length) {
          const originalFile = updatedFiles[fileIndex]
          updatedFiles[fileIndex] = new File([originalFile], uniqueFilename, {
            type: originalFile.type,
            lastModified: originalFile.lastModified,
          })
        }
      }
    }

    setRootCertificates(allCertificates)
    setRootCertificateFiles(updatedFiles)
    markDirty()
  }

  const handleDuplicateReplace = () => {
    if (!duplicateModal) return

    const updated = [...rootCertificates]
    updated[duplicateModal.existingIndex] = duplicateModal.newCertificate

    setRootCertificates(updated)
    markDirty()

    if (pendingCertificates.length) {
      processPendingCertificates(updated, pendingCertificates, pendingFileIndices)
      setPendingCertificates([])
      setPendingFileIndices([])
    }

    setDuplicateModal(null)
  }

  const handleDuplicateKeepBoth = () => {
    if (!duplicateModal) return

    const existingFilenames = rootCertificates.map(c => c.filename || '')
    const uniqueFilename = generateUniqueFilename(
      duplicateModal.newCertificate.filename || '',
      existingFilenames
    )

    const updated = [
      ...rootCertificates,
      { ...duplicateModal.newCertificate, filename: uniqueFilename },
    ]

    setRootCertificates(updated)
    
    // Update the File object in rootCertificateFiles with the new unique filename
    if (duplicateModal.fileIndex >= 0 && duplicateModal.fileIndex < rootCertificateFiles.length) {
      const updatedFiles = [...rootCertificateFiles]
      const originalFile = updatedFiles[duplicateModal.fileIndex]
      updatedFiles[duplicateModal.fileIndex] = new File([originalFile], uniqueFilename, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      })
      setRootCertificateFiles(updatedFiles)
    }
    
    markDirty()

    if (pendingCertificates.length) {
      processPendingCertificates(updated, pendingCertificates, pendingFileIndices)
      setPendingCertificates([])
      setPendingFileIndices([])
    }

    setDuplicateModal(null)
  }

  const handleDuplicateCancel = () => {
    if (pendingCertificates.length) {
      processPendingCertificates(rootCertificates, pendingCertificates, pendingFileIndices)
      setPendingCertificates([])
      setPendingFileIndices([])
    }
    setDuplicateModal(null)
  }

  const handleRemoveFile = (index: number) => {
    const updatedFiles = rootCertificateFiles.filter((_, i) => i !== index)
    setRootCertificateFiles(updatedFiles)
    
    // Find the certificate that corresponds to this file
    // Files are processed in order, so we need to find which certificate was created from this file
    // Since we add certificates to the end, we can match by finding certificates not in submittedCertificates
    const submittedFilenames = new Set(
      submittedCertificates.map(c => `${c.filename || ''}:${c.data || ''}`)
    )
    const newCertificates = rootCertificates.filter(
      c => !submittedFilenames.has(`${c.filename || ''}:${c.data || ''}`)
    )
    
    // Remove the certificate at the same relative position in new certificates
    // This works because files and certificates are processed in the same order
    const fileIndexInNew = index - (rootCertificateFiles.length - newCertificates.length)
    if (fileIndexInNew >= 0 && fileIndexInNew < newCertificates.length) {
      const updated = [
        ...submittedCertificates,
        ...newCertificates.filter((_, i) => i !== fileIndexInNew),
      ]
      setRootCertificates(updated)
      markDirty()
    }
  }

  const handleRemoveExistingCertificate = (index: number) => {
    // index is in submittedCertificates, find the corresponding certificate in rootCertificates
    const certificateToRemove = submittedCertificates[index]
    if (!certificateToRemove) return

    // Find all occurrences of this certificate in rootCertificates and mark them as removed
    const updatedRemovedIndices = new Set(removedIndices)
    rootCertificates.forEach((cert, rootIndex) => {
      if (cert.filename === certificateToRemove.filename && cert.data === certificateToRemove.data) {
        updatedRemovedIndices.add(rootIndex)
      }
    })
    setRemovedIndices(updatedRemovedIndices)
    markDirty()
  }

  const handleRestoreCertificate = (index: number) => {
    // index is in submittedCertificates, find the corresponding certificate in rootCertificates
    const certificateToRestore = submittedCertificates[index]
    if (!certificateToRestore) return

    // Remove all occurrences of this certificate from removed indices
    const updatedRemovedIndices = new Set(removedIndices)
    rootCertificates.forEach((cert, rootIndex) => {
      if (cert.filename === certificateToRestore.filename && cert.data === certificateToRestore.data) {
        updatedRemovedIndices.delete(rootIndex)
      }
    })
    setRemovedIndices(updatedRemovedIndices)
    markDirty()
  }

  const handleReset = () => {
    setRootCertificates([...submittedCertificates])
    setRootCertificateFiles([])
    setPendingCertificates([])
    setPendingFileIndices([])
    setRemovedIndices(new Set())
    setDuplicateModal(null)
    setIsDirty(false)
  }

  const handleSubmit = async () => {
    setIsSaving(true)
    try {
      // Filter out removed certificates before saving
      const certificatesToSave = rootCertificates.filter((_, index) => !removedIndices.has(index))
      await onSave(certificatesToSave)
      setSubmittedCertificates(certificatesToSave)
      setRootCertificates(certificatesToSave)
      setRootCertificateFiles([])
      setRemovedIndices(new Set())
      setIsDirty(false)
    } catch (error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Erreur lors de la sauvegarde des certificats racine'
      )
    } finally {
      setIsSaving(false)
    }
  }

  // Only show submitted certificates in the display (not newly added ones)
  // Map removed indices from rootCertificates to submittedCertificates indices
  const displayCertificates = submittedCertificates.filter(c => c.filename && c.data)
  
  // Map removed indices: find indices in submittedCertificates that correspond to removed ones in rootCertificates
  const displayRemovedIndices = new Set<number>()
  rootCertificates.forEach((cert, rootIndex) => {
    if (removedIndices.has(rootIndex)) {
      // Find this certificate in submittedCertificates
      const submittedIndex = submittedCertificates.findIndex(
        sc => sc.filename === cert.filename && sc.data === cert.data
      )
      if (submittedIndex !== -1) {
        displayRemovedIndices.add(submittedIndex)
      }
    }
  })

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
        Certificats racine
      </h3>

      <div className="space-y-6">
        <CertificateListDisplay
          certificates={displayCertificates}
          title="Certificats actuels"
          onRemove={handleRemoveExistingCertificate}
          showRemove={!readOnly}
          removedIndices={displayRemovedIndices}
          onRestore={handleRestoreCertificate}
        />

        <FileInput
          label="Ajouter des certificats racine"
          accept=".cer,.crt,.pem"
          multiple
          onChange={handleFileChange}
          selectedFiles={rootCertificateFiles}
          onFileRemove={handleRemoveFile}
          disabled={readOnly}
        />
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-2 mt-4">
          {isDirty && (
            <Button variant="outline" onClick={handleReset} disabled={isSaving}>
              Réinitialiser
            </Button>
          )}

          <Button
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!isDirty || rootCertificates.filter((_, i) => !removedIndices.has(i)).length === 0}
          >
            Enregistrer les certificats racine
          </Button>
        </div>
      )}

      {duplicateModal && (
        <DuplicateCertificateModal
          filename={duplicateModal.filename}
          onReplace={handleDuplicateReplace}
          onKeepBoth={handleDuplicateKeepBoth}
          onCancel={handleDuplicateCancel}
        />
      )}
    </div>
  )
}
